import { gql } from "@apollo/client";
import { captureException } from "@sentry/browser";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import type { PaginationDataType } from "@/components/pagination";
import apollo, { API_CMS_PROVIDER } from "@/helpers/apollo";
import City from "@/pages/city";
import type {
  FaqType,
  GameType,
  RelatedCityType,
  TextSectionType,
} from "@/pages/city/types";

interface CityType {
  about: TextSectionType | null;
  description: string;
  faq: FaqType[];
  findRooms: TextSectionType | null;
  guide: TextSectionType | null;
  howDoEscapeRoomsWork: TextSectionType | null;
  howToBook: TextSectionType | null;
  learnMore: TextSectionType | null;
  listId: number;
  nameLong: string;
  nameShort: string;
  slug: string;
  subtitle: string;
  title: string;
}

interface CityDataType {
  cities: {
    data: {
      attributes: CityType;
    }[];
  };
}

interface CityVariablesType {
  slug: string;
}

interface GamesDataType {
  gameList: {
    games: PaginationDataType<GameType>;
    geoCoordinates: {
      coordinates: number[];
    };
  };
  nearbyGameLists: {
    id: string;
  }[];
}

interface GamesVariablesType {
  id: string;
  page?: number;
}

interface RelatedCityDataType {
  cities: {
    data: RelatedCityType[];
  };
}

interface RelatedCityVariablesType {
  listIds: number[];
}

const fetchCityData = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { data: cityData } = await apollo().query<
      CityDataType,
      CityVariablesType
    >({
      context: { clientName: API_CMS_PROVIDER },
      query: gql`
        fragment TextSectionFields on ComponentGenericTextSectionWithHeader {
          body
          header
        }

        query Cities($slug: String!) {
          cities(filters: { slug: { eq: $slug } }) {
            data {
              attributes {
                about {
                  ...TextSectionFields
                }
                description
                faq {
                  answer
                  id
                  question
                }
                findRooms {
                  ...TextSectionFields
                }
                guide {
                  ...TextSectionFields
                }
                howDoEscapeRoomsWork {
                  ...TextSectionFields
                }
                howToBook {
                  ...TextSectionFields
                }
                learnMore {
                  ...TextSectionFields
                }
                listId
                nameLong
                nameShort
                slug
                subtitle
                title
              }
            }
          }
        }
      `,
      variables: { slug: data.slug },
    });

    const [city] = cityData.cities.data;

    if (!city) {
      throw notFound();
    }

    const {
      about,
      description,
      faq,
      findRooms,
      guide,
      howDoEscapeRoomsWork,
      howToBook,
      learnMore,
      listId,
      nameLong,
      nameShort,
      slug,
      subtitle,
      title,
    } = city.attributes;

    let coordinates: number[] = [];
    let games: GameType[] = [];
    let nearbyLists: number[] = [];
    let relatedCities: RelatedCityType[] = [];

    try {
      const gamesQuery = gql`
        query GameList($id: ID!, $page: Int) {
          gameList(id: $id) {
            games(page: $page) {
              pages
              objects {
                id
                bookingUrl
                awards {
                  displayTitle
                }
                communityRatingCount
                communityScoreBucket
                communityScorePercentages {
                  percentage
                  rating
                }
                description
                featuredReview {
                  summary
                  user {
                    id
                    experiencesCount
                    imageId
                    username
                  }
                }
                gameimageSet {
                  order
                  imageId
                }
                location {
                  id
                  address
                  company {
                    name
                    url
                  }
                  location {
                    coordinates
                  }
                  url
                }
                name
                minutes
                playersMax
                playersMin
                primaryCategory {
                  id
                  name
                  slug
                  parent {
                    id
                    name
                    slug
                  }
                }
                secondaryCategories {
                  id
                  name
                  slug
                }
                slug
                url
              }
            }
            geoCoordinates {
              coordinates
            }
          }
          nearbyGameLists(gameListId: $id) {
            id
          }
        }
      `;

      const { data: gamesData } = await apollo().query<
        GamesDataType,
        GamesVariablesType
      >({
        query: gamesQuery,
        variables: { id: listId.toString() },
      });

      const { gameList, nearbyGameLists } = gamesData;
      coordinates = [...gameList.geoCoordinates.coordinates].reverse();
      games = gameList.games.objects;
      nearbyLists = nearbyGameLists.map(({ id }) => Number.parseInt(id, 10));
    } catch (e) {
      captureException(e);
    }

    if (nearbyLists.length) {
      const { data: relatedCityData } = await apollo().query<
        RelatedCityDataType,
        RelatedCityVariablesType
      >({
        context: { clientName: API_CMS_PROVIDER },
        query: gql`
          query Cities($listIds: [Int]) {
            cities(filters: { listId: { in: $listIds } }) {
              data {
                id
                attributes {
                  cardImage {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  nameShort
                  slug
                }
              }
            }
          }
        `,
        variables: { listIds: nearbyLists },
      });

      relatedCities = relatedCityData.cities.data;
    }

    return {
      about,
      coordinates,
      description,
      faq,
      findRooms,
      games,
      guide,
      howDoEscapeRoomsWork,
      howToBook,
      learnMore,
      nameLong,
      nameShort,
      relatedCities,
      slug,
      subtitle,
      title,
    };
  });

export const Route = createFileRoute("/city/$slug")({
  component: CityPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { faq, slug, subtitle, title } = loaderData;
    const docTitle = `${title} - Morty: Every. Escape room. Ever.`;
    const metaDescription =
      subtitle || "24,000+ thrilling adventures to discover";

    return {
      links: [
        { href: `${process.env.APP_URL}/city/${slug}`, rel: "canonical" },
      ],
      meta: [
        { title: docTitle },
        { content: metaDescription, name: "description" },
        { content: metaDescription, property: "og:description" },
        {
          content: `${process.env.APP_URL}/images/share.jpg`,
          property: "og:image",
        },
        { content: docTitle, property: "og:title" },
        { content: "website", property: "og:type" },
        {
          content: `${process.env.APP_URL}/city/${slug}`,
          property: "og:url",
        },
        { content: "summary_large_image", name: "twitter:card" },
        { content: "@mortyapp", name: "twitter:site" },
        {
          "script:ld+json": {
            "@context": "https://schema.org/",
            "@type": "FAQPage",
            mainEntity: faq.map(
              (item: { answer: string; question: string }) => ({
                "@type": "Question",
                acceptedAnswer: { "@type": "Answer", text: item.answer },
                name: item.question,
              })
            ),
          },
        },
      ],
    };
  },
  headers: () => ({
    "Cache-Control": "s-maxage=3600, stale-while-revalidate=86400",
  }),
  loader: ({ params }) => fetchCityData({ data: { slug: params.slug } }),
});

function CityPage() {
  const data = Route.useLoaderData();
  return <City {...data} />;
}
